import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { consultationService } from '../services/consultationService'
import { userService } from '../services/userService'
import { ConsultationFormData } from '../types/consultation'
import { useAuth } from '../contexts/AuthContext'
import './QuickStartConsultationForm.css'

interface QuickStartConsultationFormProps {
  patientId: string
  appointmentId?: string // Optional appointment ID if consultation is started from an appointment
  initialStaffId?: string // Optional initial staff ID (e.g., from appointment)
  initialReason?: string // Optional initial reason (e.g., from appointment)
  onCancel?: () => void
}

export function QuickStartConsultationForm({ patientId, appointmentId, initialStaffId, initialReason, onCancel }: QuickStartConsultationFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const hasSetDefaultProvider = useRef(false)

  const [formData, setFormData] = useState<Partial<ConsultationFormData>>({
    patientId,
    staffId: initialStaffId || '',
    consultationDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    status: 'in-progress',
    reason: initialReason || '',
  })

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  // Set default provider: first use appointment staffId, then logged-in user if they are a clinical practitioner
  useEffect(() => {
    if (!hasSetDefaultProvider.current && clinicalStaff) {
      // Priority 1: Use staffId from appointment if provided
      if (initialStaffId) {
        const appointmentStaff = clinicalStaff.find((staff) => staff.id === initialStaffId)
        if (appointmentStaff) {
          setFormData((prev) => ({ ...prev, staffId: initialStaffId }))
          hasSetDefaultProvider.current = true
          return
        }
      }
      
      // Priority 2: Use logged-in user if they are a clinical practitioner
      if (user && user.roleCategory === 'Clinical') {
        const currentUserInStaff = clinicalStaff.find((staff) => staff.id === user.id)
        if (currentUserInStaff) {
          setFormData((prev) => ({ ...prev, staffId: user.id }))
          hasSetDefaultProvider.current = true
        }
      }
    }
  }, [user, clinicalStaff, initialStaffId])

  const createMutation = useMutation({
    mutationFn: (data: ConsultationFormData) => consultationService.create(data),
    onSuccess: async (consultation) => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      // Refetch appointments to reflect status change to 'in-progress'
      if (appointmentId) {
        await queryClient.refetchQueries({ queryKey: ['appointments'] })
      }
      // Navigate to consultation workspace
      navigate(`/consultations/${consultation.id}/workspace`)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.staffId) {
      alert('Please select a clinical staff member')
      return
    }

    try {
      const consultationData = {
        patientId,
        staffId: formData.staffId!,
        appointmentId: appointmentId,
        consultationDate: formData.consultationDate!,
        startTime: formData.startTime,
        status: formData.status!,
        reason: formData.reason || '',
      }
      console.log('[QuickStartConsultationForm] Creating consultation with data:', consultationData)
      await createMutation.mutateAsync(consultationData)
    } catch (error: any) {
      alert(error?.message || 'Failed to create consultation')
    }
  }

  return (
    <form className="quick-start-consultation-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <h2>Start Consultation</h2>
        <p className="form-subtitle">Quick start - you can add details later</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="staffId">
            Provider <span className="required">*</span>
          </label>
          <select
            id="staffId"
            value={formData.staffId || ''}
            onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
            required
          >
            <option value="">Select provider...</option>
            {clinicalStaff?.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} ({staff.role})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="consultationDate">Date</label>
          <input
            id="consultationDate"
            type="date"
            value={formData.consultationDate}
            onChange={(e) => setFormData({ ...formData, consultationDate: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="startTime">Start Time</label>
          <input
            id="startTime"
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="reason">Reason for Visit (Optional)</label>
        <input
          id="reason"
          type="text"
          value={formData.reason || ''}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          placeholder="e.g., Annual checkup, chest pain, follow-up"
        />
      </div>

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Starting...' : 'Start Consultation'}
        </button>
      </div>
    </form>
  )
}


























